// Selection-wrapping helpers for the composer's markdown toolbar.
// Pure DOM operations on a `<textarea>` — no React, no state. Each
// helper:
//
//   1. Reads the current `selectionStart` / `selectionEnd`.
//   2. Computes the new value + new selection range.
//   3. Writes the change through `document.execCommand('insertText')`
//      when available, so the browser's native undo stack tracks it
//      as a single undoable edit. Falls back to the React-friendly
//      descriptor-setter pattern when execCommand is unavailable
//      (Firefox refuses it on disabled fields, JSDOM has no impl).
//      The fallback fires a synthetic `input` event so React's
//      controlled-input bookkeeping picks the new value up.
//   4. Restores the focus + selection on the textarea so the next
//      keystroke continues from the expected place.
//
// Idempotency: bold / italic / inline-code wrappers detect an already-
// wrapped selection and unwrap instead — pressing Cmd+B on a bold
// selection removes the asterisks. Block helpers (`wrapLines`)
// likewise toggle the prefix.

const PLACEHOLDER_BOLD = "bold text";
const PLACEHOLDER_ITALIC = "italic text";
const PLACEHOLDER_CODE = "code";
const PLACEHOLDER_QUOTE = "quote";
const PLACEHOLDER_CODE_BLOCK = "code";

/** Result of a wrap operation — what the textarea should look like
 *  after the helper returns. Exposed for tests; in production code
 *  the helpers also push the change through to the DOM. */
export interface WrapResult {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

/** A textarea-like surface — the helpers read `value` + selection
 *  fields and write through `setRangeText` / a value setter. The
 *  `HTMLTextAreaElement` shape is what the composer passes; the
 *  interface keeps the unit tests light. */
export interface TextareaLike {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

/* ── Pure computation ───────────────────────────────────────────── */

/**
 * Compute the new value + selection for a `prefix … suffix` wrap.
 * Idempotent — when the selection is already wrapped, the wrapper is
 * removed instead. With no selection, `placeholder` is inserted between
 * the markers and selected so the user can start typing over it.
 */
export function computeWrapSelection(
  area: TextareaLike,
  prefix: string,
  suffix: string,
  placeholder: string,
): WrapResult {
  const { value, selectionStart: start, selectionEnd: end } = area;
  const selected = value.slice(start, end);

  // Inner-unwrap: the selection itself is `prefix…suffix`. Strip them.
  if (
    selected.length >= prefix.length + suffix.length &&
    selected.startsWith(prefix) &&
    selected.endsWith(suffix)
  ) {
    const inner = selected.slice(prefix.length, selected.length - suffix.length);
    const next = value.slice(0, start) + inner + value.slice(end);
    return { value: next, selectionStart: start, selectionEnd: start + inner.length };
  }

  // Outer-unwrap: the markers sit immediately around the selection.
  // Cmd+B on `foo` inside `**foo**` should still remove the wrapper.
  //
  // Run-disambiguation: when prefix === suffix and they're a single
  // repeating character (the italic case `*…*`), refuse the unwrap
  // if the candidate markers are part of a longer run of the same
  // character. Otherwise ⌘I on a selection inside `**bold**` would
  // strip one `*` from each side, silently converting bold → italic.
  const outerStart = start - prefix.length;
  const outerEnd = end + suffix.length;
  if (
    outerStart >= 0 &&
    outerEnd <= value.length &&
    value.slice(outerStart, start) === prefix &&
    value.slice(end, outerEnd) === suffix
  ) {
    const isSingleCharRun = prefix.length === 1 && prefix === suffix;
    const partOfLongerRun =
      isSingleCharRun &&
      (value.charAt(outerStart - 1) === prefix || value.charAt(outerEnd) === suffix);
    if (!partOfLongerRun) {
      const next = value.slice(0, outerStart) + selected + value.slice(outerEnd);
      return {
        value: next,
        selectionStart: outerStart,
        selectionEnd: outerStart + selected.length,
      };
    }
  }

  // Empty selection → insert placeholder between markers and select it.
  if (start === end) {
    const next = value.slice(0, start) + prefix + placeholder + suffix + value.slice(end);
    const innerStart = start + prefix.length;
    return {
      value: next,
      selectionStart: innerStart,
      selectionEnd: innerStart + placeholder.length,
    };
  }

  // Standard wrap.
  const next = value.slice(0, start) + prefix + selected + suffix + value.slice(end);
  return {
    value: next,
    selectionStart: start + prefix.length,
    selectionEnd: end + prefix.length,
  };
}

/**
 * Block-level toggle (block quote OR code block fence). Operates on
 * whole lines covered by the selection.
 *
 *   - `prefix` mode: every covered line already prefixed → strip;
 *     otherwise → prepend.
 *   - `fence` mode: opening / closing ` ``` ` lines around the whole
 *     selection; toggles off when already fenced.
 *
 * The discriminated union keeps the contract honest — a `fence` call
 * never carries an unused prefix string.
 */
export type WrapLinesOptions =
  | { mode: "prefix"; prefix: string; placeholder: string }
  | { mode: "fence"; placeholder: string };

export function computeWrapLinesSelection(
  area: TextareaLike,
  options: WrapLinesOptions,
): WrapResult {
  const { value, selectionStart: start, selectionEnd: end } = area;

  // Expand the selection to cover full lines.
  // When the user selection ends exactly on a newline boundary
  // (`end > start && value.charAt(end - 1) === '\n'`), the trailing
  // `\n` belongs to the previous line — searching for the *next*
  // newline from `end` would expand the operation onto the line
  // below, which the user did not select. Walk back one char in
  // that case so a "select the whole line by dragging" gesture
  // doesn't pull adjacent lines into the quote/fence.
  const lineStart = (() => {
    const i = value.lastIndexOf("\n", start - 1);
    return i === -1 ? 0 : i + 1;
  })();
  const lineEnd = (() => {
    const search = end > start && value.charAt(end - 1) === "\n" ? end - 1 : end;
    const i = value.indexOf("\n", search);
    return i === -1 ? value.length : i;
  })();

  const block = value.slice(lineStart, lineEnd);

  if (options.mode === "fence") {
    const placeholder = options.placeholder;
    // Code block toggle. The open/close fences are full lines
    // (` ```\n `). Toggle if the block is already fenced.
    const inner = block;
    const fenceLine = "```";
    const lines = inner.split("\n");
    const isFenced =
      lines.length >= 2 && lines[0] === fenceLine && lines[lines.length - 1] === fenceLine;

    if (isFenced) {
      const stripped = lines.slice(1, -1).join("\n");
      const next = value.slice(0, lineStart) + stripped + value.slice(lineEnd);
      return {
        value: next,
        selectionStart: lineStart,
        selectionEnd: lineStart + stripped.length,
      };
    }
    const body = inner.length === 0 ? placeholder : inner;
    const wrapped = `${fenceLine}\n${body}\n${fenceLine}`;
    const next = value.slice(0, lineStart) + wrapped + value.slice(lineEnd);
    return {
      value: next,
      selectionStart: lineStart + fenceLine.length + 1, // start of body
      selectionEnd: lineStart + fenceLine.length + 1 + body.length,
    };
  }

  // Per-line prefix toggle (block quote).
  const { prefix, placeholder } = options;
  const lines = block.split("\n");
  const hasContent = lines.some((line) => line.length > 0);
  const effective = hasContent ? lines : [placeholder];
  const allPrefixed = effective.every((line) => line.startsWith(prefix));
  const next = effective
    .map((line) => (allPrefixed ? line.slice(prefix.length) : `${prefix}${line}`))
    .join("\n");
  const replaced = value.slice(0, lineStart) + next + value.slice(lineEnd);
  return {
    value: replaced,
    selectionStart: lineStart,
    selectionEnd: lineStart + next.length,
  };
}

/**
 * Compute the value + selection for inserting a markdown link at the
 * current selection. The selected text becomes the link label; an
 * empty selection inserts a placeholder label.
 */
export function computeInsertLink(
  area: TextareaLike,
  link: { href: string; text?: string },
): WrapResult {
  const { value, selectionStart: start, selectionEnd: end } = area;
  const selected = value.slice(start, end);
  const text =
    link.text && link.text.length > 0 ? link.text : selected.length > 0 ? selected : "link";
  const insertion = `[${text}](${link.href})`;
  const next = value.slice(0, start) + insertion + value.slice(end);
  return {
    value: next,
    selectionStart: start,
    selectionEnd: start + insertion.length,
  };
}

/* ── DOM application ────────────────────────────────────────────── */

const PLACEHOLDERS = {
  bold: PLACEHOLDER_BOLD,
  italic: PLACEHOLDER_ITALIC,
  code: PLACEHOLDER_CODE,
  quote: PLACEHOLDER_QUOTE,
  codeBlock: PLACEHOLDER_CODE_BLOCK,
} as const;

/** Wrap the current selection with `prefix`/`suffix` markers,
 *  toggling if already wrapped. Preserves the native undo stack
 *  when the platform supports `document.execCommand`. */
export function wrapSelection(
  textarea: HTMLTextAreaElement,
  prefix: string,
  suffix: string,
  placeholder: string,
): void {
  const result = computeWrapSelection(textarea, prefix, suffix, placeholder);
  applyResult(textarea, result);
}

/** Block-level wrapping (quote, code block). */
export function wrapLines(textarea: HTMLTextAreaElement, options: WrapLinesOptions): void {
  const result = computeWrapLinesSelection(textarea, options);
  applyResult(textarea, result);
}

/** Insert a markdown link at the cursor. */
export function insertLink(
  textarea: HTMLTextAreaElement,
  link: { href: string; text?: string },
): void {
  const result = computeInsertLink(textarea, link);
  applyResult(textarea, result);
}

/* ── Toolbar action shortcut helpers ────────────────────────────── */

/** Bold — `**…**`. Idempotent. */
export const applyBold = (el: HTMLTextAreaElement) =>
  wrapSelection(el, "**", "**", PLACEHOLDERS.bold);

/** Italic — `*…*`. Idempotent. */
export const applyItalic = (el: HTMLTextAreaElement) =>
  wrapSelection(el, "*", "*", PLACEHOLDERS.italic);

/** Inline code — `` `…` ``. Idempotent. */
export const applyInlineCode = (el: HTMLTextAreaElement) =>
  wrapSelection(el, "`", "`", PLACEHOLDERS.code);

/** Block quote — `> ` per covered line. Idempotent. */
export const applyBlockQuote = (el: HTMLTextAreaElement) =>
  wrapLines(el, { mode: "prefix", prefix: "> ", placeholder: PLACEHOLDERS.quote });

/** Code block — fenced. Idempotent. */
export const applyCodeBlock = (el: HTMLTextAreaElement) =>
  wrapLines(el, { mode: "fence", placeholder: PLACEHOLDERS.codeBlock });

/* ── Internals ──────────────────────────────────────────────────── */

/**
 * Apply a `WrapResult` to a real textarea. Tries `execCommand` first
 * so the browser's undo stack tracks the change as a single edit;
 * falls back to a value-setter that fires a synthetic `input` event
 * so React's controlled-input pipeline notices the change.
 *
 * IME composition: bail without replacing if the textarea currently
 * holds an in-progress IME composition. Replacing the textarea content
 * mid-composition discards the candidate. Callers should re-issue the
 * action after `compositionend`. The composer's keydown handler
 * already gates the Enter path on `isComposing`; this guard mirrors
 * the contract for toolbar buttons and shortcuts.
 */
function applyResult(el: HTMLTextAreaElement, result: WrapResult): void {
  if (isComposing(el)) return;
  const replacement = result.value;
  // Preserve focus before any replacement; setRangeText / execCommand
  // can otherwise drop it on focused-but-not-active branches.
  if (typeof el.focus === "function") el.focus();

  // Path 1: native execCommand. Selecting the entire current value
  // and running insertText replaces it as a single undo step.
  if (
    typeof document !== "undefined" &&
    typeof document.execCommand === "function" &&
    typeof el.setSelectionRange === "function"
  ) {
    el.setSelectionRange(0, el.value.length);
    const ok = safeExecInsertText(replacement);
    if (ok) {
      el.setSelectionRange(result.selectionStart, result.selectionEnd);
      return;
    }
  }

  // Path 2: descriptor-setter fallback. Calling the prototype's
  // value setter directly is the standard React-controlled-input
  // workaround — it bypasses React's input bookkeeping caching so
  // the synthetic `input` event re-sets the value through React.
  const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, "value")?.set;
  if (setter) {
    setter.call(el, replacement);
  } else {
    el.value = replacement;
  }
  if (typeof el.setSelectionRange === "function") {
    el.setSelectionRange(result.selectionStart, result.selectionEnd);
  }
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

function safeExecInsertText(text: string): boolean {
  try {
    return document.execCommand("insertText", false, text);
  } catch {
    return false;
  }
}

/** True when the element is the active IME-composition target. The
 *  DOM exposes this through a non-standard `isComposing` flag on the
 *  most recent input/composition event; we read it via a property
 *  the composer's `onCompositionStart`/`End` handlers (when added)
 *  would set on the element. Today we approximate via the standard
 *  `event.isComposing` flag captured on the element's data set. */
function isComposing(el: HTMLTextAreaElement): boolean {
  // Safari + Chromium expose `isComposing` only on the keyboard /
  // composition event objects, not on the element. The composer
  // doesn't currently track composition state on the textarea, so
  // we fall back to a `data-composing` attribute that future
  // composition handlers can set. Returning false is the safe
  // default — toolbar actions still work outside IME flows.
  return el.dataset.composing === "true";
}
